import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use the HH favicon for header logo
  const headerLogo = '/favicon.png';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
  ];

  const firmName = language === 'tr' ? 'HH Hukuk Bürosu' : 'HH Law Office';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0f1f35] ${
        isScrolled ? 'shadow-lg' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div
            className="cursor-pointer flex items-center gap-3"
            onClick={() => scrollToSection('hero')}
          >
            <img 
              src={headerLogo} 
              alt={firmName}
              className="h-12 w-12 object-contain rounded-lg"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-blue-200 hover:text-white font-medium transition-colors"
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-blue-200 hover:text-white font-medium transition-colors"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-blue-200 hover:text-white font-medium transition-colors"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('blog')}
              className="text-blue-200 hover:text-white font-medium transition-colors"
            >
              {t('nav.blog')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-blue-200 hover:text-white font-medium transition-colors"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Right Side - Language & CTA */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors outline-none px-3 py-2 rounded-lg hover:bg-white/10">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium uppercase">{language}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      language === lang.code ? 'text-[#1e3a5f] font-medium bg-blue-50' : 'text-gray-700'
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button - Desktop */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden lg:flex items-center gap-2 bg-white text-[#1e3a5f] px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors shadow-sm"
            >
              {t('hero.cta')}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#1e3a5f] border-t border-white/10 py-6">
            <nav className="flex flex-col gap-2">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-blue-200 hover:text-white hover:bg-white/10 font-medium transition-colors text-left px-4 py-3 rounded-lg"
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-blue-200 hover:text-white hover:bg-white/10 font-medium transition-colors text-left px-4 py-3 rounded-lg"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-blue-200 hover:text-white hover:bg-white/10 font-medium transition-colors text-left px-4 py-3 rounded-lg"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('blog')}
                className="text-blue-200 hover:text-white hover:bg-white/10 font-medium transition-colors text-left px-4 py-3 rounded-lg"
              >
                {t('nav.blog')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-blue-200 hover:text-white hover:bg-white/10 font-medium transition-colors text-left px-4 py-3 rounded-lg"
              >
                {t('nav.contact')}
              </button>
              <div className="px-4 pt-4">
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-white text-[#1e3a5f] px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  {t('hero.cta')}
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
